import React, { useMemo, useState } from 'react';
import { CheckIcon, EllipsisIcon, ExternalLinkIcon, FilePlus2Icon, FileText, LockKeyhole, Menu, Search, SlidersHorizontal, DownloadIcon } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { downloadCsv } from '../data/csv';
import { NewInvoiceDialog, type NewInvoiceDraft } from '../components/invoices/NewInvoiceDialog';
import { Sidebar } from '../components/Sidebar';
import { INITIAL_JOBS, type Invoice } from './Jobs';
import { useScreenInit } from '../useScreenInit';
type InvoiceRecord = Invoice & {
  jobName: string;
  jobRef: string;
  client: string;
};
const formatCurrency = (value: number) => new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0
}).format(value);
export function Invoices() {
  useScreenInit();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Mirrors the local admin convention used across the existing app.
  const [isAdmin] = useState(true);
  const [query, setQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<Invoice['paymentStatus'] | ''>('');
  const [raisedFrom, setRaisedFrom] = useState('');
  const [raisedTo, setRaisedTo] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isNewInvoiceOpen, setIsNewInvoiceOpen] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<InvoiceRecord[]>(() => INITIAL_JOBS.flatMap(job => (job.invoices || []).map(invoice => ({
    ...invoice,
    jobName: job.name,
    jobRef: job.ref,
    client: job.client
  }))).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
  const clients = useMemo(() => [...new Set(invoices.map(invoice => invoice.client))].sort(), [invoices]);
  const departments = useMemo(() => [...new Set(invoices.map(invoice => invoice.department))].sort(), [invoices]);
  const categories = useMemo(() => [...new Set(invoices.map(invoice => invoice.category))].sort(), [invoices]);
  const statuses = useMemo(() => (['paid', 'unpaid', 'cancelled'] as const).filter(status => invoices.some(invoice => invoice.paymentStatus === status)), [invoices]);
  const hasAdvancedFilters = Boolean(clientFilter || departmentFilter || categoryFilter || statusFilter || raisedFrom || raisedTo);
  const hasActiveFilters = Boolean(query || hasAdvancedFilters);
  const clearFilters = () => {
    setQuery('');
    setClientFilter('');
    setDepartmentFilter('');
    setCategoryFilter('');
    setStatusFilter('');
    setRaisedFrom('');
    setRaisedTo('');
  };
  const markAsPaid = (invoiceKey: string) => {
    setInvoices(current => current.map(invoice => invoiceKey === getInvoiceKey(invoice) ? {
      ...invoice,
      paymentStatus: 'paid'
    } : invoice));
    setOpenActionMenu(null);
  };
  const createInvoice = (draft: NewInvoiceDraft) => {
    const project = INITIAL_JOBS.find(job => job.id === draft.projectId);
    if (!project) return;
    setInvoices(current => [{
      id: `inv-${Math.random().toString(36).slice(2, 10)}`,
      value: draft.value,
      category: draft.category,
      department: draft.department,
      paymentStatus: draft.paymentStatus,
      sageId: draft.sageId,
      description: draft.description,
      addedBy: 'Matthew Carter',
      date: `${draft.date}T12:00:00.000Z`,
      jobName: project.name,
      jobRef: project.ref,
      client: project.client
    }, ...current].sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime()));
    setIsNewInvoiceOpen(false);
  };
  const filteredInvoices = invoices.filter(invoice => {
    const needle = query.toLowerCase().trim();
    const raisedDate = invoice.date.slice(0, 10);
    const matchesQuery = !needle || invoice.jobName.toLowerCase().includes(needle) || invoice.jobRef.toLowerCase().includes(needle) || invoice.client.toLowerCase().includes(needle) || invoice.department.toLowerCase().includes(needle) || invoice.sageId?.toLowerCase().includes(needle);
    return matchesQuery && (!clientFilter || invoice.client === clientFilter) && (!departmentFilter || invoice.department === departmentFilter) && (!categoryFilter || invoice.category === categoryFilter) && (!statusFilter || invoice.paymentStatus === statusFilter) && (!raisedFrom || raisedDate >= raisedFrom) && (!raisedTo || raisedDate <= raisedTo);
  });
  const activeInvoices = filteredInvoices.filter(invoice => invoice.paymentStatus !== 'cancelled');
  const exportInvoices = () => {
    downloadCsv('invoices-export.csv', [{
      label: 'Job ref',
      value: invoice => invoice.jobRef
    }, {
      label: 'Job',
      value: invoice => invoice.jobName
    }, {
      label: 'Client',
      value: invoice => invoice.client
    }, {
      label: 'Department',
      value: invoice => invoice.department
    }, {
      label: 'Category',
      value: invoice => invoice.category
    }, {
      label: 'Date raised',
      value: invoice => invoice.date.slice(0, 10)
    }, {
      label: 'Sage ID',
      value: invoice => invoice.sageId
    }, {
      label: 'Status',
      value: invoice => invoice.paymentStatus
    }, {
      label: 'Value',
      value: invoice => invoice.value
    }, {
      label: 'Description',
      value: invoice => invoice.description
    }, {
      label: 'Added by',
      value: invoice => invoice.addedBy
    }], filteredInvoices);
  };
  const invoicedTotal = activeInvoices.reduce((sum, invoice) => sum + invoice.value, 0);
  const creditNotesTotal = activeInvoices.filter(invoice => invoice.value < 0).reduce((sum, invoice) => sum + invoice.value, 0);
  const paidTotal = activeInvoices.filter(invoice => invoice.value > 0 && invoice.paymentStatus === 'paid').reduce((sum, invoice) => sum + invoice.value, 0);
  const outstandingTotal = activeInvoices.filter(invoice => invoice.value > 0 && invoice.paymentStatus === 'unpaid').reduce((sum, invoice) => sum + invoice.value, 0);
  if (!isAdmin) {
    return <div className="flex h-screen w-screen overflow-hidden bg-[#1F5C9E] font-sans" data-id="element-1066" data-mp="taghg3pr0wj2">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={isAdmin} data-id="element-1067" data-mp="17zcqa2zmqdkq" />
        <main className="flex-1 min-w-0 w-full p-0 md:py-3 md:pr-3 overflow-hidden flex" data-id="element-1068" data-mp="136jkdbsfcz72">
          <div className="flex-1 bg-white md:rounded-2xl shadow-xl flex items-center justify-center p-6" data-id="element-1069" data-mp="fh8pgaxr9z0u">
            <div className="max-w-sm text-center" data-id="element-1070" data-mp="aixge2fabsjo">
              <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-500" data-id="element-1071" data-mp="2m729phkup7so">
                <LockKeyhole className="h-6 w-6" data-id="element-1072" data-mp="2qykb26qsdae6" />
              </span>
              <h1 className="text-xl font-semibold text-slate-900" data-id="element-1073" data-mp="2c8wbb9y96jsk">
                Invoices restricted
              </h1>
              <p className="mt-2 text-sm text-slate-500" data-id="element-1074" data-mp="2h8r1fba7f5ck">
                Finance access is limited to administrators.
              </p>
            </div>
          </div>
        </main>
      </div>;
  }
  return <div className="flex h-screen w-screen overflow-hidden bg-[#1F5C9E] font-sans" data-id="element-1075" data-mp="39v22so5mxaaf">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={isAdmin} data-id="element-1076" data-mp="2vvtxremsj7yb" />
      <main className="flex-1 min-w-0 w-full p-0 md:py-3 md:pr-3 overflow-hidden flex" data-id="element-1077" data-mp="2zwdxmpyr0po5">
        <div className="flex-1 min-w-0 w-full bg-white md:rounded-2xl shadow-xl flex flex-col overflow-hidden" data-id="element-1078" data-mp="1kfvmd44549tx">
          <header className="p-5 md:p-8 md:pb-6 border-b border-slate-100" data-id="element-1079" data-mp="1ogxktiq8ghuu">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden flex items-center gap-2 text-[#14466B] mb-4" aria-label="Open menu" data-id="element-1080" data-mp="2g7ejau8igng7">
              <Menu className="w-6 h-6" data-id="element-1081" data-mp="276gkqrgud850" />
            </button>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-1082" data-mp="227dcyz9h8ntc">
              <div data-id="element-1083" data-mp="2yxmcokmerzj7">
                <h1 className="text-2xl font-bold text-slate-900" data-id="element-1084" data-mp="2yw1k30jnmqnz">Invoices</h1>
                <p className="text-sm text-slate-500 mt-1" data-id="element-1085" data-mp="2uuzk2hhwyaky">
                  A finance view of department-level job invoices and credit
                  notes.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2" data-id="element-1086" data-mp="2l67rq88xxo9i">
                <button type="button" onClick={exportInvoices} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100" title="Export filtered invoices as CSV" data-id="element-1087" data-mp="2l3vda52e5w17"><DownloadIcon className="h-4 w-4" data-id="element-1088" data-mp="3q4lacb3n7gcq" />Export CSV</button>
                <button type="button" onClick={() => setIsNewInvoiceOpen(true)} className="inline-flex w-fit items-center gap-2 rounded-lg bg-[#6A9A24] px-3.5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#587F1D] focus:outline-none focus:ring-2 focus:ring-[#6A9A24]/30 focus:ring-offset-2" data-id="element-1089" data-mp="3q4ux7pte57e7">
                  <FilePlus2Icon className="h-4 w-4" data-id="element-1090" data-mp="cw8fuy6pp7qc" />
                  New invoice
                </button>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-1091" data-mp="moe4pz1o391b">
              <FinanceStat label="Net invoiced" value={invoicedTotal} data-id="element-1092" data-mp="mpp6xufgs8qb" />
              <FinanceStat label="Paid" value={paidTotal} tone="paid" data-id="element-1093" data-mp="qr2c02aqg7w6" />
              <FinanceStat label="Outstanding" value={outstandingTotal} tone="outstanding" data-id="element-1094" data-mp="vpm28ewd05uc" />
              <FinanceStat label="Credit notes" value={creditNotesTotal} negative data-id="element-1095" data-mp="o0zi5nzttce4" />
            </div>
          </header>

          <div className="p-5 md:px-8 md:pt-6 md:pb-4" data-id="element-1096" data-mp="xyx4u721f14m">
            <div className="relative flex items-end gap-3" data-id="element-1097" data-mp="12ryqcxwr1yx6">
              <div className="relative min-w-0 flex-1" data-id="element-1098" data-mp="16tbu92ga7s1g">
                <label htmlFor="invoice-search" className="sr-only" data-id="element-1099" data-mp="16simpz9i0yi9">
                  Search invoices, jobs or clients
                </label>
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-1100" data-mp="1grhulfmfwq47" />
                <input id="invoice-search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search invoices, jobs or clients..." className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-id="element-1101" data-mp="1lr47fnycw4ch" />
              </div>
              <button type="button" onClick={() => setIsFiltersOpen(open => !open)} aria-expanded={isFiltersOpen} aria-controls="invoice-filter-popout" aria-label="Toggle invoice filters" className={`relative inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-100 ${isFiltersOpen || hasAdvancedFilters ? 'border-[#14466B] bg-[#14466B] text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-1102" data-mp="1zu04u5p5e1mb">
                <SlidersHorizontal className="h-4 w-4" data-id="element-1103" data-mp="135u2xqjm1nbq" />
                {hasAdvancedFilters && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-amber-400 ring-2 ring-[#14466B]" data-id="element-1104" data-mp="y70re3nvw6e7" />}
              </button>

              {isFiltersOpen && <section id="invoice-filter-popout" className="absolute right-0 top-[calc(100%+0.75rem)] z-20 w-full max-w-3xl rounded-xl border border-slate-200 bg-white p-4 shadow-xl md:p-5" aria-labelledby="invoice-filters-heading" data-id="element-1105" data-mp="14mr71j6syolu">
                  <div className="mb-4 flex items-center justify-between gap-3" data-id="element-1106" data-mp="gwwuz3ub542n">
                    <div className="flex items-center gap-2" data-id="element-1107" data-mp="lo55y4bacxhx">
                      <SlidersHorizontal className="h-4 w-4 text-[#14466B]" data-id="element-1108" data-mp="lpq2fzh5uqif" />
                      <h2 id="invoice-filters-heading" className="text-sm font-semibold text-slate-900" data-id="element-1109" data-mp="7xrwfxiqxaqh">
                        Filter invoices
                      </h2>
                    </div>
                    <button type="button" onClick={clearFilters} disabled={!hasActiveFilters} className="text-sm font-medium text-[#14466B] hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline" data-id="element-1110" data-mp="2xdyj8auzh66">
                      Clear filters
                    </button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1111" data-mp="3kfb3ovlbjj0w">
                    <FilterSelect id="invoice-client-filter" label="Client" value={clientFilter} onChange={setClientFilter} placeholder="All clients" options={clients} data-id="element-1112" data-mp="2a7v30w58ovq" />
                    <FilterSelect id="invoice-department-filter" label="Department" value={departmentFilter} onChange={setDepartmentFilter} placeholder="All departments" options={departments} data-id="element-1113" data-mp="72hlsj531sz2" />
                    <FilterSelect id="invoice-category-filter" label="Category" value={categoryFilter} onChange={setCategoryFilter} placeholder="All categories" options={categories} data-id="element-1114" data-mp="2agif4m8nzlpq" />
                    <FilterSelect id="invoice-status-filter" label="Status" value={statusFilter} onChange={value => setStatusFilter(value as Invoice['paymentStatus'] | '')} placeholder="All statuses" options={statuses} formatOption={formatStatusLabel} data-id="element-1115" data-mp="1rka93g7tv6td" />
                    <div data-id="element-1116" data-mp="3gy59o22lr92e">
                      <label htmlFor="invoice-raised-from" className="mb-1 block text-xs font-medium text-slate-600" data-id="element-1117" data-mp="3gxndt6juqwv0">
                        Raised from
                      </label>
                      <input id="invoice-raised-from" type="date" value={raisedFrom} max={raisedTo || undefined} onChange={event => setRaisedFrom(event.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-id="element-1118" data-mp="11budhpzbjydr" />
                    </div>
                    <div data-id="element-1119" data-mp="8lcasq2ibz0v">
                      <label htmlFor="invoice-raised-to" className="mb-1 block text-xs font-medium text-slate-600" data-id="element-1120" data-mp="8m47k47uc2sq">
                        Raised to
                      </label>
                      <input id="invoice-raised-to" type="date" value={raisedTo} min={raisedFrom || undefined} onChange={event => setRaisedTo(event.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-id="element-1121" data-mp="2m3gq4nwfz4wr" />
                    </div>
                  </div>
                </section>}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-auto px-5 md:px-8 pb-8 md:pb-10" data-id="element-1122" data-mp="2vtrw0fzoro6a">
            {filteredInvoices.length === 0 ? <div className="h-full min-h-[320px] flex flex-col items-center justify-center text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1123" data-mp="30r1vypkpbi8d">
                <FileText className="w-12 h-12 text-slate-300 mb-4" data-id="element-1124" data-mp="1pdg5n133val9" />
                <h2 className="text-lg font-medium text-slate-900" data-id="element-1125" data-mp="1htnd5nt7qhgl">
                  No invoices found
                </h2>
                <p className="mt-1 text-sm text-slate-500" data-id="element-1126" data-mp="1cv3rzpmg19pa">
                  Try a different search or department filter.
                </p>
              </div> : <div className="border border-slate-200 rounded-xl overflow-auto" data-id="element-1127" data-mp="1lv8ublcs5hwl">
                <table className="min-w-[840px] w-full text-left border-collapse" data-id="element-1128" data-mp="1ltfp8ahtf5qp">
                  <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200" data-id="element-1129" data-mp="y5xrlybvddft">
                    <tr data-id="element-1130" data-mp="u5xf4j8bzcbe">
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1131" data-mp="17vhbn7bbjdc9">
                        Job
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1132" data-mp="1337sqb8mfdjh">
                        Client
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1133" data-mp="agvbww0spuny">
                        Department
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1134" data-mp="3q0znn8jxsxcl">
                        Category
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1135" data-mp="3v2f8p0m473uz">
                        Date
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1136" data-mp="8scnyl71g2ce">
                        Sage ID
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1137" data-mp="hkxtgzlnscja">
                        Status
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right" data-id="element-1138" data-mp="mmdfatvnun2q">
                        Value
                      </th>
                      <th className="w-14 px-3 py-3" data-id="element-1139" data-mp="wiyl3hfi827m">
                        <span className="sr-only" data-id="element-1140" data-mp="2h1ajpcde966z">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-1141" data-mp="2c03bjc7ca4zs">
                    {filteredInvoices.map(invoice => {
                  const isCredit = invoice.value < 0;
                  const isPaid = invoice.paymentStatus === 'paid';
                  const isCancelled = invoice.paymentStatus === 'cancelled';
                  return <tr key={`${invoice.jobRef}-${invoice.id}`} className="hover:bg-slate-50 transition-colors" data-id="element-1142" data-mp="2urw0mvjaf628">
                          <td className="px-4 py-3" data-id="element-1143" data-mp="2l2vyj39hnujt">
                            <p className="text-sm font-medium text-slate-900" data-id="element-1144" data-mp="2l09sx965rp48">
                              {invoice.jobName}
                            </p>
                            <p className="mt-0.5 text-xs text-slate-500" data-id="element-1145" data-mp="3dpyvhqyinx7y">
                              {invoice.jobRef}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600" data-id="element-1146" data-mp="136vc9ixzn6xo">
                            {invoice.client}
                          </td>
                          <td className="px-4 py-3" data-id="element-1147" data-mp="z7w13glnydxs">
                            <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700" data-id="element-1148" data-mp="u7gsf2cbtum5">
                              {invoice.department}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-id="element-1149" data-mp="1mxqf41hpy8jm">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${isCredit ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-700'}`} data-id="element-1150" data-mp="1i3cp6eltstmc">
                              {invoice.category}
                            </span>
                            {invoice.description && <p className="mt-1 max-w-48 truncate text-xs text-slate-500" title={invoice.description} data-id="element-1151" data-mp="2d5ms08sr6ok5">{invoice.description}</p>}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600" data-id="element-1152" data-mp="2i6ka6ldrjiln">
                            {new Date(invoice.date).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-700" data-id="element-1153" data-mp="35v3ih0wtbvdq">
                            {invoice.sageId || <span className="text-slate-300" data-id="element-1154" data-mp="3artwzjsyyix1">—</span>}
                          </td>
                          <td className="px-4 py-3" data-id="element-1155" data-mp="3ew1otpuq4ezh">
                            {!isCredit && <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${isCancelled ? 'bg-slate-200 text-slate-600' : isPaid ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-1156" data-mp="3jul6he2ab4m1">
                                {isCancelled ? 'Cancelled' : isPaid ? 'Paid' : 'Outstanding'}
                              </span>}
                          </td>
                          <td className={`px-4 py-3 text-sm font-semibold text-right tabular-nums ${isCredit ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-1157" data-mp="1yit1tkv3uc59">
                            {isCredit ? `−${formatCurrency(Math.abs(invoice.value))}` : formatCurrency(invoice.value)}
                          </td>
                          <td className="px-3 py-3 text-right" data-id="element-1158" data-mp="2oqqrfmr5frzr">
                            <InvoiceActionsMenu invoice={invoice} isOpen={openActionMenu === getInvoiceKey(invoice)} onToggle={() => setOpenActionMenu(current => current === getInvoiceKey(invoice) ? null : getInvoiceKey(invoice))} onClose={() => setOpenActionMenu(null)} onMarkAsPaid={() => markAsPaid(getInvoiceKey(invoice))} data-id="element-1159" data-mp="2osjyh35oce8a" />
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>}
          </div>
        </div>
      </main>
      <AnimatePresence data-id="element-1160" data-mp="36nhuvhy0569u">
        {isNewInvoiceOpen && <NewInvoiceDialog projects={INITIAL_JOBS.map(job => ({
        id: job.id,
        ref: job.ref,
        name: job.name,
        client: job.client,
        departments: job.departments
      }))} onClose={() => setIsNewInvoiceOpen(false)} onCreate={createInvoice} data-id="element-1161" data-mp="31owwzp682yok" />}
      </AnimatePresence>
    </div>;
}
function InvoiceActionsMenu({
  invoice,
  isOpen,
  onToggle,
  onClose,
  onMarkAsPaid
}: {
  invoice: InvoiceRecord;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onMarkAsPaid: () => void;
}) {
  const cannotMarkAsPaid = invoice.paymentStatus !== 'unpaid' || invoice.value <= 0;
  const cannotViewInvoice = !invoice.fileUrl;
  return <div className="relative inline-flex" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      onClose();
    }
  }} data-id="element-1162" data-mp="1zzt2e3i1u7y8">
      <button type="button" onClick={onToggle} aria-label={`Actions for ${invoice.jobName} invoice`} aria-expanded={isOpen} aria-haspopup="menu" className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-1163" data-mp="jjdmvm1yj7dh">
        <EllipsisIcon className="h-4 w-4" data-id="element-1164" data-mp="svvplwrwju8" />
      </button>
      {isOpen && <div role="menu" aria-label={`Invoice actions for ${invoice.jobName}`} className="absolute right-0 top-[calc(100%+0.375rem)] z-20 w-44 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 text-left shadow-lg" data-id="element-1165" data-mp="qt818j3f2so">
          <button type="button" role="menuitem" disabled={cannotMarkAsPaid} onClick={onMarkAsPaid} className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent" data-id="element-1166" data-mp="397we1o67oe4r">
            <CheckIcon className="h-4 w-4" data-id="element-1167" data-mp="3fx5rng9jcvnt" />
            Mark as paid
          </button>
          {cannotViewInvoice ? <span role="menuitem" aria-disabled="true" className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-400" data-id="element-1168" data-mp="3az470qwgooa8">
              <ExternalLinkIcon className="h-4 w-4" data-id="element-1169" data-mp="2j7omd2egtbmn" />
              View invoice
            </span> : <a href={invoice.fileUrl} target="_blank" rel="noopener noreferrer" role="menuitem" onClick={onClose} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50" data-id="element-1170" data-mp="2x7zf6azc50ag">
              <ExternalLinkIcon className="h-4 w-4" data-id="element-1171" data-mp="7jgm23bitjgj" />
              View invoice
            </a>}
        </div>}
    </div>;
}
function getInvoiceKey(invoice: InvoiceRecord) {
  return `${invoice.jobRef}-${invoice.id}`;
}
type FilterSelectProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  options: readonly string[];
  formatOption?: (option: string) => string;
};
function FilterSelect({
  id,
  label,
  value,
  onChange,
  placeholder,
  options,
  formatOption = option => option
}: FilterSelectProps) {
  return <div data-id="element-1172" data-mp="2d64l7onen6hi">
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-slate-600" data-id="element-1173" data-mp="1zcwx46yj8t4k">
        {label}
      </label>
      <select id={id} value={value} onChange={event => onChange(event.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-id="element-1174" data-mp="24aye8mceeide">
        <option value="" data-id="element-1175" data-mp="wr322yasu1tj">{placeholder}</option>
        {options.map(option => <option key={option} value={option} data-id="element-1176" data-mp="ryt4cw3fzxgp">
            {formatOption(option)}
          </option>)}
      </select>
    </div>;
}
function formatStatusLabel(status: string) {
  if (status === 'unpaid') return 'Outstanding';
  return status.charAt(0).toUpperCase() + status.slice(1);
}
function FinanceStat({
  label,
  value,
  negative = false,
  tone = 'default'
}: {
  label: string;
  value: number;
  negative?: boolean;
  tone?: 'default' | 'paid' | 'outstanding' | 'cancelled';
}) {
  const displayValue = value < 0 ? `−${formatCurrency(Math.abs(value))}` : formatCurrency(value);
  const valueClassName = negative || value < 0 ? 'text-rose-600' : tone === 'paid' ? 'text-emerald-700' : tone === 'outstanding' ? 'text-amber-700' : tone === 'cancelled' ? 'text-slate-600' : 'text-slate-900';
  return <div className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-id="element-1177" data-mp="21xuju19210m2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400" data-id="element-1178" data-mp="25ywavusti6cy">
        {label}
      </p>
      <p className={`mt-0.5 text-sm font-bold tabular-nums ${valueClassName}`} data-id="element-1179" data-mp="2axhb3u82kq1e">
        {displayValue}
      </p>
    </div>;
}